import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { accessToken } from '../../../../configuration/tokenHandling/accessToken';
import type { Discounts } from '../../common/discount/redux/types';
import { getMarketplaceBackend } from '../../config/pathURL/config';
import { decodeJson, onRejected } from '../api';
import { FetchError } from '../FetchError';
import { ApiDiscountsCodec } from './discount.types';
import { mapApiDiscounts } from './discountMapper';

export const discountApiSlice = createApi({
    reducerPath: 'discountApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getMarketplaceBackend(),
        prepareHeaders: headers => {
            const token = accessToken.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Discounts'],
    endpoints: builder => ({
        getDiscounts: builder.query<Discounts, { productId?: string; level?: string } | undefined>({
            query: params => {
                const queryParams = new URLSearchParams();
                if (params?.productId) {
                    queryParams.append('product_id', params.productId);
                }
                if (params?.level) {
                    queryParams.append('level', params.level);
                }
                const queryString = queryParams.toString();
                return `/discounts${queryString ? `?${queryString}` : ''}`;
            },
            transformResponse: async (response: unknown) => {
                try {
                    const decoded = decodeJson(ApiDiscountsCodec)(response);
                    return mapApiDiscounts(decoded);
                } catch (error) {
                    throw new FetchError({ ok: false, status: 500 } as Response, JSON.stringify(error));
                }
            },
            transformErrorResponse: (response: { status: number; data: unknown }) => {
                const error = new FetchError(
                    { ok: false, status: response.status } as Response,
                    JSON.stringify(response.data)
                );
                return onRejected([401])(error);
            },
            providesTags: ['Discounts'],
        }),
    }),
});

export const { useGetDiscountsQuery } = discountApiSlice;
